import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Clock, 
  Eye, 
  X, 
  Edit3
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const labelMap = {
  kicker: 'House System Section Tag / Kicker',
  title: 'House System Main Heading',
  subtitle: 'House System Subheading',
  description: 'House System Overview Description'
};

export default function StudentLifeHouseSystemForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    subtitle: '',
    description: '',
    houses: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false,
    description: false
  });

  const [pendingHouses, setPendingHouses] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [editingHouseIndex, setEditingHouseIndex] = useState(null);
  const [isAddingHouse, setIsAddingHouse] = useState(false);
  const [modalHouseData, setModalHouseData] = useState(null);
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [valuesText, setValuesText] = useState('');
  const [activitiesText, setActivitiesText] = useState('');
  const [achievementsText, setAchievementsText] = useState('');

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let housesSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          housesSection = result.data.find(s => s.section_type === 'student-life-house-system');
        }
      }

      if (!housesSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            housesSection = result.data.find(s => s.section_type === 'student-life-house-system');
          }
        }
      }

      if (!housesSection) {
        throw new Error('House system section not found');
      }

      setSectionId(housesSection.id);

      const content = housesSection.content_published || housesSection.content || {};
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsedContent.en || parsedContent;

      let normalizedHouses = [];
      if (Array.isArray(enData.houses)) {
        normalizedHouses = enData.houses.map((h, idx) => ({
          name: h.name || h.title || `House ${idx + 1}`,
          color: h.color || '#2563eb',
          motto: h.motto || '',
          image: h.image || h.logo || h.crest || '',
          description: h.description || '',
          houseLeader: h.houseLeader || '',
          members: h.members || '',
          values: Array.isArray(h.values) ? h.values : [],
          activities: Array.isArray(h.activities) ? h.activities : [],
          achievements: Array.isArray(h.achievements) ? h.achievements : []
        }));
      }

      const initialData = {
        kicker: enData.kicker || 'Student Leadership & Community',
        title: enData.title || 'The House System',
        subtitle: enData.subtitle || 'Belonging, leadership, and school pride',
        description: enData.description || 'Our vibrant house system connects students across grade levels through friendly competition, mentorship, school spirit, and camaraderie.',
        houses: normalizedHouses
      };

      const pendingFieldsMap = { kicker: false, title: false, subtitle: false, description: false };
      const pendingHousesMap = {};

      const pendingRes = await fetch(`${apiBase}/cms/section/${housesSection.id}/with-pending`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        if (pendingData.success && pendingData.data) {
          const pendingChanges = pendingData.data.pending_changes || [];
          
          if (Array.isArray(pendingChanges)) {
            pendingChanges.forEach((change) => {
              if (change.status === 'pending' && change.locale === 'en') {
                let val = change.new_value;
                if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'subtitle' || change.field_id === 'description') {
                  pendingFieldsMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                } else if (change.field_id && change.field_id.includes('houses.')) {
                  const match = change.field_id.match(/houses\.(\d+)\.(add|delete|update)/);
                  if (match) {
                    const houseIdx = parseInt(match[1], 10);
                    const op = match[2];
                    if (op === 'delete') {
                      pendingHousesMap[`house_${houseIdx}_delete`] = true;
                    } else if (op === 'add') {
                      pendingHousesMap[`house_${houseIdx}_add`] = true;
                      if (val && !initialData.houses[houseIdx]) {
                        initialData.houses[houseIdx] = val;
                      }
                    } else {
                      pendingHousesMap[`house_${houseIdx}`] = true;
                      if (val && initialData.houses[houseIdx]) {
                        initialData.houses[houseIdx] = val;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
      setPendingHouses(pendingHousesMap);
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Load error:', err);
      toast.error('Error loading house system section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddHouse = () => {
    const newHouse = {
      name: '',
      color: '#2563eb',
      motto: '',
      image: '',
      description: '',
      houseLeader: '',
      members: '',
      values: [],
      activities: [],
      achievements: []
    };
    setIsAddingHouse(true);
    setEditingHouseIndex(formData.houses.length);
    setValuesText('');
    setActivitiesText('');
    setAchievementsText('');
    setModalHouseData(newHouse);
  };

  const handleOpenHouseModal = (index) => {
    setIsAddingHouse(false);
    setEditingHouseIndex(index);
    const target = formData.houses[index] || {};
    const vt = Array.isArray(target.values) ? target.values.join('\n') : (target.values || '');
    const at = Array.isArray(target.activities) ? target.activities.join('\n') : (target.activities || '');
    const ac = Array.isArray(target.achievements) ? target.achievements.join('\n') : (target.achievements || '');
    setValuesText(vt);
    setActivitiesText(at);
    setAchievementsText(ac);
    setModalHouseData({
      ...target,
      name: target.name || '',
      color: target.color || '#2563eb',
      motto: target.motto || '',
      image: target.image || target.logo || target.crest || '',
      description: target.description || '',
      houseLeader: target.houseLeader || '',
      members: target.members || '',
      values: Array.isArray(target.values) ? [...target.values] : [],
      activities: Array.isArray(target.activities) ? [...target.activities] : [],
      achievements: Array.isArray(target.achievements) ? [...target.achievements] : []
    });
  };

  const handleCloseHouseModal = () => {
    setEditingHouseIndex(null);
    setModalHouseData(null);
    setValuesText('');
    setActivitiesText('');
    setAchievementsText('');
    setIsAddingHouse(false);
  };

  const handleDeleteHouse = async (index) => {
    if (pendingHouses[`house_${index}`] || pendingHouses[`house_${index}_delete`] || pendingHouses[`house_${index}_add`]) {
      toast.info('This house has a pending approval');
      return;
    }

    const houseToDelete = formData.houses[index];
    if (!window.confirm(`Delete house "${houseToDelete.name || `House ${index + 1}`}"? This request will be submitted to Principal for approval.`)) return;

    const houseKey = `house_${index}`;
    setSubmitting(prev => ({ ...prev, [houseKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `houses.${index}.delete`,
          field_name: `Delete House: ${houseToDelete.name || `House ${index + 1}`}`,
          old_value: houseToDelete || {},
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Deletion request for "${houseToDelete.name || `House ${index + 1}`}" submitted for Principal approval`);
        setPendingHouses(prev => ({
          ...prev,
          [`house_${index}_delete`]: true
        }));
      } else {
        toast.error(result.error || result.message || 'Failed to submit house deletion');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [houseKey]: false }));
    }
  };

  const handleImageUpload = (houseIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(prev => ({ ...prev, [houseIndex]: true }));
    setImageProgress(prev => ({ ...prev, [houseIndex]: 0 }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('section_type', 'student-life-house-system');

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setImageProgress(prev => ({ ...prev, [houseIndex]: percent }));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          if (response.success || response.ok) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '');
            const returnedUrl = response.url || response.file_path || (response.filename ? `/api/uploads/studentlife/${response.filename}` : '');
            const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
            const finalUrl = fullUrl || returnedUrl || String(response.media_id || response.id || '');

            setModalHouseData(prev => ({
              ...prev,
              image: finalUrl
            }));

            const mediaId = response.media_id || response.id;
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl
              }));
            }

            toast.success('House crest uploaded successfully');
          } else {
            toast.error(response.error || response.message || 'Upload failed');
          }
        } catch (pe) {
          toast.error('Invalid server response');
        }
      } else {
        toast.error(`Upload failed (HTTP ${xhr.status})`);
      }
      setImageUploading(prev => ({ ...prev, [houseIndex]: false }));
    });

    xhr.addEventListener('error', () => {
      toast.error('Network error during upload');
      setImageUploading(prev => ({ ...prev, [houseIndex]: false }));
    });

    xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
    xhr.withCredentials = true;
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(uploadFormData);
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: labelMap[fieldName] || fieldName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitHouse = async (houseIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!modalHouseData || !modalHouseData.name?.trim()) {
      toast.warning('Please enter a house name');
      return;
    }

    const cleanValues = (valuesText || '').split('\n').map(v => v.trim()).filter(Boolean);
    const cleanActivities = (activitiesText || '').split('\n').map(a => a.trim()).filter(Boolean);
    const cleanAchievements = (achievementsText || '').split('\n').map(a => a.trim()).filter(Boolean);

    const house = {
      ...modalHouseData,
      name: (modalHouseData.name || '').trim(),
      motto: (modalHouseData.motto || '').trim(),
      color: (modalHouseData.color || '#2563eb').trim(),
      image: modalHouseData.image || '',
      description: (modalHouseData.description || '').trim(),
      houseLeader: (modalHouseData.houseLeader || '').trim(),
      members: (modalHouseData.members || '').trim(),
      values: cleanValues,
      activities: cleanActivities,
      achievements: cleanAchievements
    };

    const isAdd = isAddingHouse;
    const fieldId = isAdd ? `houses.${houseIndex}.add` : `houses.${houseIndex}.update`;
    const fieldName = isAdd ? `Add House: ${house.name}` : `Update House: ${house.name}`;
    const oldValue = isAdd ? null : (originalData.houses[houseIndex] || {});

    const houseKey = `house_${houseIndex}`;
    setSubmitting(prev => ({ ...prev, [houseKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: house,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`House "${house.name}" submitted for Principal approval`);
        
        if (isAdd) {
          setFormData(prev => ({
            ...prev,
            houses: [...prev.houses, house]
          }));
          setOriginalData(prev => ({
            ...prev,
            houses: [...prev.houses, house]
          }));
          setPendingHouses(prev => ({ ...prev, [`house_${houseIndex}_add`]: true }));
        } else {
          const updatedHouses = [...formData.houses];
          updatedHouses[houseIndex] = house;
          setFormData(prev => ({ ...prev, houses: updatedHouses }));
          setOriginalData(prev => {
            const nextOrig = [...(prev.houses || [])];
            nextOrig[houseIndex] = house;
            return { ...prev, houses: nextOrig };
          });
          setPendingHouses(prev => ({ ...prev, [houseKey]: true }));
        }

        handleCloseHouseModal();
      } else {
        if (response.status === 409) {
          toast.error('This house already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [houseKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading House System section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Shield size={22} />
        </div>
        <div>
          <h2 className="unified-title">House System & Teams</h2>
          <p className="unified-subtitle">
            Manage house teams, mottos, colors, crest badges, and community structure
          </p>
        </div>
      </div>

      {/* Field 1: Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={80}
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Student Leadership & Community"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={120}
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. The House System"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Subheading / Tagline</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={150}
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Belonging, leadership, and school pride"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Section Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength={600}
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="Explain how the house system fosters fellowship, healthy competition, and leadership..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 5: Houses List */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">House Teams ({formData.houses.length})</label>
            <p className="unified-field-desc">
              Houses representing courage, fellowship, spirit competition, and school values
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddHouse}
          >
            <Plus size={14} /> Add House
          </button>
        </div>

        {formData.houses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <Shield size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No houses configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add House" above to create school houses</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.houses.map((house, idx) => {
              const isPendingDelete = pendingHouses[`house_${idx}_delete`];
              const isPendingAdd = pendingHouses[`house_${idx}_add`];
              const isPendingUpdate = pendingHouses[`house_${idx}`];
              const hasPending = isPendingDelete || isPendingAdd || isPendingUpdate;

              const houseImgUrl = getMediaUrl(house.image);

              let borderColor = '#e2e8f0';
              if (isPendingDelete) borderColor = '#ef4444';
              else if (hasPending) borderColor = '#f59e0b';

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${borderColor}`,
                    borderLeft: `5px solid ${house.color || '#2563eb'}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.75 : 1
                  }}
                >
                  {/* Badge Preview */}
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '10px',
                      background: '#0f172a',
                      overflow: 'hidden',
                      flexShrink: 0,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    {houseImgUrl ? (
                      <img
                        src={houseImgUrl}
                        alt={house.name || `House ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Shield size={24} color={house.color || '#cbd5e1'} />
                    )}
                    {houseImgUrl && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage(houseImgUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '3px',
                          right: '3px',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Preview crest badge"
                      >
                        <Eye size={10} />
                      </button>
                    )}
                  </div>

                  {/* House Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a' }}>
                        {house.name || `House #${idx + 1}`}
                      </span>
                      {house.color && (
                        <span
                          style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: house.color,
                            border: '1px solid #cbd5e1'
                          }}
                          title={`Theme color: ${house.color}`}
                        />
                      )}
                      {isPendingDelete && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px', background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' }}>
                          <Clock size={10} /> Pending Deletion
                        </span>
                      )}
                      {isPendingAdd && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Creation
                        </span>
                      )}
                      {isPendingUpdate && !isPendingDelete && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      )}
                    </div>
                    {house.motto && (
                      <p style={{ margin: '0 0 3px', fontSize: '13px', color: '#475569', fontStyle: 'italic' }}>
                        "{house.motto}"
                      </p>
                    )}
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                      {(house.values || []).length} values &bull; {(house.activities || []).length} activities &bull; {(house.achievements || []).length} achievements
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      disabled={isPendingDelete}
                      onClick={() => handleOpenHouseModal(idx)}
                    >
                      <Edit3 size={13} /> Edit
                    </button>
                    {!hasPending && (
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        disabled={submitting[`house_${idx}`]}
                        onClick={() => handleDeleteHouse(idx)}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit / Add House Modal */}
      {editingHouseIndex !== null && modalHouseData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseHouseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                {isAddingHouse ? 'Add New House' : `Edit House: ${modalHouseData.name || `#${editingHouseIndex + 1}`}`}
              </h3>
              <button
                type="button"
                onClick={handleCloseHouseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>House Name *</label>
                  <input
                    type="text"
                    value={modalHouseData.name || ''}
                    onChange={(e) => setModalHouseData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. House of Courage"
                    className="unified-input"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Motto</label>
                  <input
                    type="text"
                    value={modalHouseData.motto || ''}
                    onChange={(e) => setModalHouseData(prev => ({ ...prev, motto: e.target.value }))}
                    placeholder="e.g. Courage Unlocks Dreams"
                    className="unified-input"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Theme Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="color"
                        value={modalHouseData.color || '#2563eb'}
                        onChange={(e) => setModalHouseData(prev => ({ ...prev, color: e.target.value }))}
                        style={{ width: '42px', height: '40px', padding: '0', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={modalHouseData.color || '#2563eb'}
                        onChange={(e) => setModalHouseData(prev => ({ ...prev, color: e.target.value }))}
                        className="unified-input"
                        style={{ maxWidth: '140px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>House Leader / Coordinator</label>
                    <input
                      type="text"
                      value={modalHouseData.houseLeader || ''}
                      onChange={(e) => setModalHouseData(prev => ({ ...prev, houseLeader: e.target.value }))}
                      placeholder="e.g. Mr. James Wilson"
                      className="unified-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Members Info</label>
                  <input
                    type="text"
                    value={modalHouseData.members || ''}
                    onChange={(e) => setModalHouseData(prev => ({ ...prev, members: e.target.value }))}
                    placeholder="e.g. Approximately 180 students across all levels"
                    className="unified-input"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Overview Description</label>
                  <textarea
                    value={modalHouseData.description || ''}
                    onChange={(e) => setModalHouseData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the spirit, heritage, and values that define this house..."
                    className="unified-textarea"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Core Values (one per line)</label>
                  <textarea
                    value={valuesText}
                    onChange={(e) => setValuesText(e.target.value)}
                    placeholder={"Brave leadership\nFearless determination\nResilience and strength\nBold innovation"}
                    className="unified-textarea"
                    rows={3}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Press Enter to separate points. Each line renders as a core value.
                  </span>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>House Activities (one per line)</label>
                  <textarea
                    value={activitiesText}
                    onChange={(e) => setActivitiesText(e.target.value)}
                    placeholder={"Leadership development programs\nCourage workshops and seminars\nSports training and competitions"}
                    className="unified-textarea"
                    rows={3}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Press Enter to separate points.
                  </span>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Achievements & Recognition (one per line)</label>
                  <textarea
                    value={achievementsText}
                    onChange={(e) => setAchievementsText(e.target.value)}
                    placeholder={"Leadership Excellence Award 2024\nCourage in Action Recognition\nSports Championship Winners"}
                    className="unified-textarea"
                    rows={3}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Press Enter to separate points.
                  </span>
                </div>

                {/* House Crest / Badge */}
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>House Crest / Badge Photo</label>
                  {modalHouseData.image && getMediaUrl(modalHouseData.image) && (
                    <div style={{ width: '90px', height: '90px', borderRadius: '10px', overflow: 'hidden', marginBottom: '10px', background: '#0f172a', border: '1px solid #cbd5e1' }}>
                      <img
                        src={getMediaUrl(modalHouseData.image)}
                        alt="Badge preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <label className="unified-secondary-btn" style={{ cursor: imageUploading[editingHouseIndex] ? 'not-allowed' : 'pointer', display: 'inline-flex' }}>
                    <Upload size={14} />
                    <span>
                      {imageUploading[editingHouseIndex] 
                        ? `Uploading (${imageProgress[editingHouseIndex] || 0}%)...` 
                        : modalHouseData.image ? 'Change Crest Photo' : 'Choose Crest Photo'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleImageUpload(editingHouseIndex, e)}
                      disabled={imageUploading[editingHouseIndex]}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseHouseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitHouse(editingHouseIndex)}
                disabled={submitting[`house_${editingHouseIndex}`] || !modalHouseData.name || imageUploading[editingHouseIndex]}
              >
                <Save size={14} />
                {submitting[`house_${editingHouseIndex}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10
              }}
            >
              <X size={16} />
            </button>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
